import type {
  ShopifyOrderSkipReason,
  ShopifyOrderView,
  WebOrderAttentionReason,
} from "../../api/types";
import { whenText } from "./report/webOrdersText";

/**
 * G-160 (OD-263) — how the office names and colours each reason a web order
 * needs someone. The reason itself comes from the server (one rule); this file
 * only says it, so the Web orders list, its cards and the Orders page panel
 * never word it two ways.
 */
export type AttentionTone = "blocked" | "soon" | "info";

export const REASON_LABEL: Record<WebOrderAttentionReason, string> = {
  CANT_BE_MADE: "Can't be made",
  NOT_LINKED: "Not linked",
  NOTHING_LINKED: "Nothing linked",
  DELIVERY_FAILED: "Courier couldn't deliver",
  OVERDUE_NOT_BUILT: "Overdue, not built",
  STOCK_TO_MOVE: "Stock to move",
  CHANGED_IN_SHOPIFY: "Changed in Shopify",
  MULTI_BOUQUET_LINE: "2+ bouquets on a line",
  DUE_TODAY_NOT_BUILT: "Due today, not built",
  DUE_TOMORROW_NOT_BUILT: "Due tomorrow, not built",
  WAITING: "Waiting for a shop",
};

export const REASON_TONE: Record<WebOrderAttentionReason, AttentionTone> = {
  CANT_BE_MADE: "blocked",
  NOT_LINKED: "blocked",
  NOTHING_LINKED: "blocked",
  DELIVERY_FAILED: "blocked",
  OVERDUE_NOT_BUILT: "blocked",
  STOCK_TO_MOVE: "soon",
  CHANGED_IN_SHOPIFY: "soon",
  MULTI_BOUQUET_LINE: "soon",
  DUE_TODAY_NOT_BUILT: "soon",
  DUE_TOMORROW_NOT_BUILT: "soon",
  WAITING: "info",
};

/** Filter chip order: most urgent first. */
export const REASON_ORDER: WebOrderAttentionReason[] = [
  "CANT_BE_MADE",
  "NOT_LINKED",
  "NOTHING_LINKED",
  "DELIVERY_FAILED",
  "OVERDUE_NOT_BUILT",
  "STOCK_TO_MOVE",
  "CHANGED_IN_SHOPIFY",
  "MULTI_BOUQUET_LINE",
  "DUE_TODAY_NOT_BUILT",
  "DUE_TOMORROW_NOT_BUILT",
  "WAITING",
];

export const SKIP_REASON_LABEL: Record<ShopifyOrderSkipReason, string> = {
  MADE_OUTSIDE_CRM: "Made outside the CRM",
  CANCELLED_WITH_CUSTOMER: "Cancelled with the customer",
  DUPLICATE: "Duplicate order",
  TEST: "Test order",
  OTHER: "Other",
};

/** The class of a reason's tag (`.wo-attn` in ui.css). */
export function toneClass(reason: WebOrderAttentionReason): string {
  return `wo-attn is-${REASON_TONE[reason]}`;
}

/** An order's most urgent tone, or null when it needs no one. */
export function orderTone(order: Pick<ShopifyOrderView, "attention">): AttentionTone | null {
  const tones = order.attention.map((reason) => REASON_TONE[reason]);
  if (tones.includes("blocked")) return "blocked";
  if (tones.includes("soon")) return "soon";
  return tones.length ? "info" : null;
}

const TONE_RANK: Record<AttentionTone, number> = { blocked: 0, soon: 1, info: 2 };

/** The reason a row leads with: the first of its most urgent tone. */
export function leadReason(order: Pick<ShopifyOrderView, "attention">): WebOrderAttentionReason | null {
  const tone = orderTone(order);
  return order.attention.find((reason) => REASON_TONE[reason] === tone) ?? null;
}

/** Most urgent first; orders that need no one last. Stable, so ties keep their order. */
export function byUrgency(a: Pick<ShopifyOrderView, "attention">, b: Pick<ShopifyOrderView, "attention">): number {
  const rank = (order: Pick<ShopifyOrderView, "attention">) => {
    const tone = orderTone(order);
    return tone ? TONE_RANK[tone] : 3;
  };
  return rank(a) - rank(b);
}

const qty = (fixed: string): string => String(Number(fixed));

/** One line saying what a reason means for THIS order. */
export function reasonDetail(
  order: ShopifyOrderView,
  reason: WebOrderAttentionReason,
  now: Date = new Date(),
): string {
  switch (reason) {
    case "CANT_BE_MADE":
      // G-167: an order the office placed keeps its shop; its shortfall was
      // found on the preparation day.
      return (
        (order.prepShortfall ?? order.feasibility?.shortfall ?? []).map((line) => line.text).join("; ") ||
        "Short in every shop"
      );
    case "NOT_LINKED": {
      const titles = order.lines
        .filter((line) => line.kind === "UNLINKED" || (line.kind === "ITEM" && !line.itemId))
        .map((line) => `"${line.titleRaw}"`);
      return `${titles.join(", ")} not linked`;
    }
    case "NOTHING_LINKED":
      return `${order.lines.length} ${order.lines.length === 1 ? "product" : "products"}, none linked`;
    case "DELIVERY_FAILED":
      return `${order.delivery?.courierName ?? "The courier"}: "${order.delivery?.failureReason ?? ""}"`;
    case "STOCK_TO_MOVE": {
      const pieces = (order.transferPlan ?? []).map((transfer) =>
        transfer.packaging
          ? `a roll of ${transfer.description} from ${transfer.fromLocationName}`
          : `${qty(transfer.qty)} ${transfer.description} from ${transfer.fromLocationName}`,
      );
      const shop = order.claimedLocation?.name ?? order.offeredToLocation?.name;
      return `${shop ? `${shop} · ` : ""}bring ${pieces.join(", ")}`;
    }
    case "CHANGED_IN_SHOPIFY":
      return (order.shopifyChange?.changes ?? []).join("; ");
    case "MULTI_BOUQUET_LINE": {
      const line = order.lines.find((l) => l.kind === "BOUQUET" && Number(l.quantity) > 1);
      return line ? `"${line.titleRaw}" × ${qty(line.quantity)} on one line` : "";
    }
    case "OVERDUE_NOT_BUILT":
    case "DUE_TODAY_NOT_BUILT":
    case "DUE_TOMORROW_NOT_BUILT": {
      const unbuilt = order.lines.filter((line) => line.kind === "BOUQUET" && !line.bouquetBuildId);
      const who = [order.claimedBy?.fullName, order.claimedLocation?.name].filter(Boolean).join(", ");
      return [
        order.requestedFor ? `Wanted ${whenText(order.requestedFor)}` : null,
        `${unbuilt.map((line) => line.titleRaw).join(", ")} to build`,
        who || null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "WAITING": {
      const since = new Date(order.offeredAt ?? order.surfacedAt ?? order.receivedAt);
      const minutes = Math.max(0, Math.floor((now.getTime() - since.getTime()) / 60_000));
      return `Offered ${minutes} min ago · nobody accepted`;
    }
  }
}
